/* eslint-disable react/react-in-jsx-scope */
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { FileInput, FileUploader } from "@/components/extension/file-upload";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { useLogoStore } from "@/dashboard/store/store";
import useImageCompressor from "@/hooks/useImageCompressor";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { Camera, ImageIcon, Loader2, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

const ManageLogo = () => {
  const { handleCompressImage } = useImageCompressor();
  const { postMultipart } = usePostMultipart();
  const { postData } = usePostData<any>();
  const { user, updateUserState } = useAuth();
  const hidePostActions = useHidePostActions();
  const { logoUrl, setLogoUrl } = useLogoStore();

  const [uploadLogoLoading, setUploadLogoLoading] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  const resolvedLogoUrl = useMemo(
    () => logoUrl || user?.logo?.uri || "",
    [logoUrl, user?.logo?.uri],
  );

  const syncLogoInStorage = (nextLogo: { uri: string } | null) => {
    if (user) {
      updateUserState({
        ...user,
        logo: nextLogo,
      });
      return;
    }

    try {
      const raw = localStorage.getItem("user");
      if (!raw) return;
      const parsed = JSON.parse(raw);
      parsed.logo = nextLogo;
      localStorage.setItem("user", JSON.stringify(parsed));
    } catch {
      // noop
    }
  };

  const handleRemoveLogo = async () => {
    setRemovingLogo(true);
    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/theme`,
      data: {
        logo: "clear",
      },
    });

    if (res !== null) {
      setLogoUrl("");
      syncLogoInStorage(null);
      toast({
        title: "Le logo personnalisé a bien été supprimé.",
        duration: 1200,
      });
    }

    setRemovingLogo(false);
  };

  const handleSubmit = async (media: { id: number; uri: string }) => {
    const res = await postData({
      url: `${import.meta.env.VITE_IP_API}/api/theme`,
      data: {
        logo: media.id,
      },
    });

    if (res === null) {
      return false;
    }

    setLogoUrl(media.uri);
    syncLogoInStorage({ uri: media.uri });
    toast({
      title: "Le logo personnalisé a bien été enregistré.",
      duration: 1200,
    });
    return true;
  };

  const handlePictureChange = async (value: File[] | null) => {
    if (!value?.length) {
      return;
    }

    setUploadLogoLoading(true);
    const data = new FormData();
    data.append("type", "10");

    const compressedBlob = await handleCompressImage(value[0], 500, 300, 0.9);
    const fileToUpload = compressedBlob ?? value[0];
    data.append("files[]", fileToUpload, value[0].name);

    const newMedias = await postMultipart({
      url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
      data,
    });

    if (
      !Array.isArray(newMedias) &&
      newMedias.success !== undefined &&
      newMedias.success !== null &&
      !newMedias.success
    ) {
      toast({
        title: newMedias.message,
        duration: 1200,
      });
      setUploadLogoLoading(false);
      return;
    }

    if (Array.isArray(newMedias) && newMedias[0]) {
      await handleSubmit(newMedias[0]);
    }

    setUploadLogoLoading(false);
  };

  if (hidePostActions) return null;

  const isBusy = uploadLogoLoading || removingLogo;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-slate-900">
            Logo du portail
          </p>
          <p className="text-xs leading-5 text-slate-500">
            Importez un logo pour personnaliser le menu latéral et les pages
            liées à votre espace.
          </p>
        </div>
        {resolvedLogoUrl ? (
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
            Personnalisé
          </span>
        ) : (
          <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-500">
            Par défaut
          </span>
        )}
      </div>

      <div className="mt-4 flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
          {resolvedLogoUrl ? (
            <img
              src={import.meta.env.VITE_MEDIA_BASE + resolvedLogoUrl}
              alt="Logo personnalisé"
              className="h-full w-full object-contain"
            />
          ) : (
            <img
              src="/logo.svg"
              alt="Logo Verdeo"
              className="h-10 w-10 object-contain opacity-90"
            />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">
            {resolvedLogoUrl ? "Logo actuellement affiché" : "Logo Verdeo par défaut"}
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-500">
            Format conseillé : image carrée ou horizontale, légère et lisible
            sur fond clair.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <FileUploader
          value={[]}
          onValueChange={handlePictureChange}
          dropzoneOptions={{ multiple: false }}
          className="inline-flex"
        >
          <FileInput className="inline-flex">
            <Button
              type="button"
              variant="outline"
              className="gap-2"
              disabled={isBusy}
            >
              {uploadLogoLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
              {resolvedLogoUrl ? "Remplacer le logo" : "Ajouter un logo"}
            </Button>
          </FileInput>
        </FileUploader>

        {resolvedLogoUrl ? (
          <Button
            type="button"
            variant="destructive"
            className="gap-2"
            disabled={isBusy}
            onClick={() => void handleRemoveLogo()}
          >
            {removingLogo ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Supprimer le logo
          </Button>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
            <ImageIcon className="h-3.5 w-3.5" />
            Aucun logo personnalisé enregistré
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageLogo;
